import { useState } from "react";
import { motion } from "framer-motion";
import { Check, Gift, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const includes = [
  "Session 1: Orientation (Parents welcome)",
  "Session 2: Discovery & Debrief (1-to-1)",
  "Session 3: Career Choice Mapping (1-to-1)",
  "4 optional quarterly check-ins over 1 year",
  "Scientific psychometric assessment",
  "Detailed career report with debrief",
  "Exclusive TheKhoj welcome kit",
];

export default function Pricing() {
  const [showEnrol, setShowEnrol] = useState(false);
  const [enrolForm, setEnrolForm] = useState({ student_name: "", parent_email: "" });
  const [paying, setPaying] = useState(false);

  const startPayment = async (e) => {
    e.preventDefault();
    setPaying(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        package_id: "career_exploration_program",
        origin_url: window.location.origin,
        student_name: enrolForm.student_name,
        parent_email: enrolForm.parent_email,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setPaying(false);
    }
  };

  return (
    <section id="pricing" className="py-20 sm:py-28 bg-secondary/50" data-testid="pricing-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="max-w-2xl mb-14"
        >
          <p className="text-xs font-mono uppercase tracking-[0.25em] text-primary font-semibold mb-4">Investment</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
            Simple, honest <span className="text-accent">pricing</span>
          </h2>
        </motion.div>

        <div className="flex justify-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7 }}
            className="bg-primary text-primary-foreground rounded-3xl p-8 sm:p-10 flex flex-col shadow-2xl shadow-primary/30 relative overflow-hidden w-full max-w-2xl"
            data-testid="pricing-core-card"
          >
            <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-accent/20 blur-2xl" />
            <p className="text-xs font-mono uppercase tracking-[0.25em] font-semibold opacity-80">Career Exploration Program</p>
            <div className="mt-5 flex items-end gap-2">
              <span className="font-display text-5xl sm:text-6xl font-extrabold">₹10,000</span>
              <span className="text-sm opacity-80 mb-2">/ student</span>
            </div>
            <p className="text-[11px] opacity-70 mt-1">plus applicable taxes</p>
            <p className="mt-3 text-sm opacity-90 leading-relaxed">
              The complete journey — three core sessions, a year of quarterly check-ins, scientific assessment, detailed report and a welcome kit.
            </p>
            <ul className="mt-7 space-y-3 flex-1">
              {includes.map((item) => (
                <li key={item} className="flex items-start gap-3 text-sm">
                  <Check className="w-4 h-4 mt-0.5 shrink-0 text-accent" />
                  {item}
                </li>
              ))}
            </ul>
            <button
              onClick={() => setShowEnrol(true)}
              data-testid="pricing-core-cta"
              className="mt-8 inline-flex w-full items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-accent text-accent-foreground font-semibold text-sm hover:opacity-90 transition-opacity"
            >
              <Gift className="w-4 h-4" /> Enrol Your Child — Pay ₹10,000
            </button>
          </motion.div>

        </div>
      </div>

      {showEnrol && (
        <div className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm flex items-center justify-center px-4" data-testid="enrol-modal">
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            className="w-full max-w-md bg-card border border-border rounded-3xl p-8 shadow-2xl"
          >
            <div className="flex items-start justify-between mb-2">
              <h3 className="font-display text-xl font-bold">Enrol in the Program</h3>
              <button onClick={() => setShowEnrol(false)} aria-label="Close" data-testid="enrol-modal-close"
                className="w-8 h-8 rounded-full hover:bg-secondary flex items-center justify-center">
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="text-xs text-muted-foreground mb-6">
              Career Exploration Program · ₹10,000 (plus applicable taxes) · Secure checkout
            </p>
            <form onSubmit={startPayment} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold mb-1.5">Student Name *</label>
                <input required value={enrolForm.student_name}
                  onChange={(e) => setEnrolForm({ ...enrolForm, student_name: e.target.value })}
                  className="w-full rounded-xl border border-input bg-background px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder="e.g. Aarav Sharma" data-testid="enrol-student-name" />
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1.5">Your Email *</label>
                <input required type="email" value={enrolForm.parent_email}
                  onChange={(e) => setEnrolForm({ ...enrolForm, parent_email: e.target.value })}
                  className="w-full rounded-xl border border-input bg-background px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder="For receipt & session coordination" data-testid="enrol-parent-email" />
              </div>
              <button type="submit" disabled={paying}
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-primary text-primary-foreground font-semibold text-sm hover:opacity-90 transition-opacity disabled:opacity-60"
                data-testid="enrol-pay-button">
                {paying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Gift className="w-4 h-4" />}
                {paying ? "Opening secure checkout…" : "Proceed to Secure Payment"}
              </button>
              <p className="text-[11px] text-muted-foreground text-center">You'll be redirected to a secure payment page.</p>
            </form>
          </motion.div>
        </div>
      )}
    </section>
  );
}
